import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Lock, AlertCircle, Clock } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import IndianPhoneInput from '../components/IndianPhoneInput';
import '../components/IndianPhoneInput.css';
import useSeo from '../hooks/useSeo';

/** Extract 10-digit Indian number from E.164 value (+919876543210 → 9876543210) */
const getCleanPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 10) return digits;
  return digits;
};

const Login = () => {
  useSeo({
    title: 'Login — KnowMySchemes',
    description: 'Login to KnowMySchemes to save schemes and jobs, run KMS Smart analyses, and track your subscription.',
    canonical: 'https://knowmyschemes.com/login',
    noindex: true,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t } = useLanguage();

  const wasInactive = searchParams.get('reason') === 'inactivity';

  const [formData, setFormData] = useState(() => {
    const savedPhone = localStorage.getItem('rememberedPhone');
    return { phone: savedPhone || '', password: '' };
  });
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('rememberedPhone'));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInactivityMessage, setShowInactivityMessage] = useState(wasInactive);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setShowInactivityMessage(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const cleanedPhone = getCleanPhone(formData.phone);

    if (!cleanedPhone || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }
    if (cleanedPhone.length !== 10 || !/^\d+$/.test(cleanedPhone)) {
      setError('Please enter a valid 10-digit phone number');
      setLoading(false);
      return;
    }

    if (rememberMe) {
      localStorage.setItem('rememberedPhone', formData.phone);
    } else {
      localStorage.removeItem('rememberedPhone');
    }

    const result = await login(cleanedPhone, formData.password);
    setLoading(false);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-4 py-8 sm:p-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <Card className="w-full max-w-md p-5 sm:p-8 shadow-2xl mx-2 sm:mx-0">
        <div className="flex justify-center mb-4 sm:mb-6">
          <img src="/logo.jpeg" alt="Logo" className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl shadow-lg object-cover" />
        </div>

        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent mb-2" data-testid="login-title">
            {t('welcomeBack')}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">{t('loginToAccess')}</p>
        </div>

        {showInactivityMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-amber-700">You were logged out due to inactivity. Please login again to continue.</p>
          </div>
        )}

        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="login-error">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">{t('phoneNumber')}</label>
            <IndianPhoneInput
              value={formData.phone}
              onChange={(val) => { setFormData({ ...formData, phone: val || '' }); setError(''); setShowInactivityMessage(false); }}
              testId="login-phone-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">{t('password')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              <input
                type="password" name="password" value={formData.password} onChange={handleChange}
                placeholder={t('passwordPlaceholder')}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input type="checkbox" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer" />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-600 cursor-pointer select-none">{t('rememberMe') || 'Remember me'}</label>
          </div>

          <Button type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-2.5 sm:py-3 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
            data-testid="login-submit-btn">
            {loading ? t('loggingIn') : t('login')}
          </Button>
        </form>

        <div className="mt-3 text-center">
          <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-green-600 transition" data-testid="forgot-password-link">{t('forgotPassword')}</Link>
        </div>

        <div className="mt-5 sm:mt-6 text-center">
          <p className="text-gray-600 text-sm sm:text-base">
            {t('noAccount')}{' '}
            <Link to="/signup" className="text-green-600 font-semibold hover:text-green-700 transition">{t('signup')}</Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Login;
